import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { initializeAssistant } from 'modules/chatbot/services/innitializationService';
import {
  setInitializationStatus,
  setInitializationError,
  setShowPreparingOverlay,
  setContext,
  selectInitializationStatus,
  selectInitializationError,
  selectHasContext,
  selectContext,
  ApiStatus,
} from 'modules/chatbot/slices/chatbotSlice';
import { computeNextContext } from 'modules/chatbot/utils/utils';

interface UseInitializationOptions {
  benefitGuideId: string;
  employerId: string;
  organizationId: string;
  autoInitialize?: boolean;
}

export const useInitialization = ({
  benefitGuideId,
  employerId,
  organizationId,
  autoInitialize = false,
}: UseInitializationOptions) => {
  const dispatch = useAppDispatch();
  const initializationStatus = useAppSelector(selectInitializationStatus);
  const initializationError = useAppSelector(selectInitializationError);
  const hasContext = useAppSelector(selectHasContext);
  const currentContext = useAppSelector(selectContext);
  const idCardData = useAppSelector((state) => state.idCard);

  const performInitialization = useCallback(async () => {
    if (!benefitGuideId || !employerId || !organizationId) {
      dispatch(setInitializationError('Missing benefitGuideId or employerId'));
      return;
    }

    try {
      dispatch(setInitializationStatus(ApiStatus.LOADING));
      dispatch(setShowPreparingOverlay(true));
      dispatch(setInitializationError(null));

      const response = await initializeAssistant(
        benefitGuideId,
        employerId,
        organizationId
      );
      dispatch(setContext({ ...response.data }));
      dispatch(setInitializationStatus(ApiStatus.SUCCESS));
      dispatch(setShowPreparingOverlay(false));
    } catch (error: any) {
      dispatch(setInitializationStatus(ApiStatus.ERROR));
      dispatch(
        setInitializationError(
          error?.message || 'Failed to initialize assistant'
        )
      );
      // Keep overlay open on error so user can retry
    }
  }, [benefitGuideId, employerId, organizationId, dispatch]);

  const initialize = useCallback(async () => {
    if (autoInitialize) {
      await performInitialization();
    }
  }, [autoInitialize, performInitialization]);

  const retry = useCallback(() => {
    performInitialization();
  }, [performInitialization]);

  const reset = useCallback(() => {
    dispatch(setInitializationStatus(ApiStatus.IDLE));
    dispatch(setInitializationError(null));
    dispatch(setShowPreparingOverlay(false));
  }, [dispatch]);

  // Auto-initialize on mount if enabled, not already initialized, and no context exists
  // Append/remove ID card data to/from context based on login state
  useEffect(() => {
    if (!hasContext) return;

    const next = computeNextContext({
      idCardData,
      currentContext,
      unmaskMemberId: true,
    });

    if (next) {
      dispatch(setContext(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    idCardData?.isLoggedIn,
    idCardData?.electionMDVIdCardData,
    hasContext,
    dispatch,
  ]);

  return {
    initialize,
    retry,
    reset,
    status: initializationStatus,
    error: initializationError,
    isLoading: initializationStatus === ApiStatus.LOADING,
    isSuccess: initializationStatus === ApiStatus.SUCCESS,
    isError: initializationStatus === ApiStatus.ERROR,
  };
};
